import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchBuddies, fetchRecipes } from "../lib/api";
import { Heart, Utensils, ArrowRight } from "lucide-react";

export default function MeetTheBuddies() {
  const [buddies, setBuddies] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchBuddies().then(setBuddies).catch((err) => console.warn("[fetch] failed:", err));
    fetchRecipes().then(setRecipes).catch((err) => console.warn("[fetch] failed:", err));
  }, []);

  const recipeByBuddy = useMemo(() => {
    const map = {};
    recipes.forEach((r) => { if (r.buddy_id) map[r.buddy_id] = r; });
    return map;
  }, [recipes]);

  return (
    <section id="buddies" data-testid="section-buddies" className="relative py-20 md:py-28 bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">Meet the Buddies</p>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 leading-tight">
            Six little characters. <br /> Six little ways to <span className="wavy">love food again</span>.
          </h2>
          <p className="mt-5 text-pb-soft text-lg">
            Each Buddy isn't just a face on a mat — they're a personality, a coordinating recipe, and a different way to invite your kid into the meal.
          </p>
        </div>

        <div className="mt-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-24 pt-16">
          {buddies.map((b) => (
            <button
              key={b.id}
              data-testid={`card-buddy-${b.id}`}
              onClick={() => setActive(b)}
              className="text-left group relative rounded-[2rem] p-7 pt-44 bg-white border-[4px] hover:-translate-y-2 transition-all duration-300 overflow-visible"
              style={{ borderColor: b.color, boxShadow: `0 18px 40px ${b.color}22` }}
            >
              {/* Tinted blob behind the avatar — keeps per-Buddy color
                  personality while leaving the card body white (readable). */}
              <div
                className="absolute -right-10 -top-10 h-44 w-44 rounded-full opacity-40 pointer-events-none"
                style={{ background: b.color }}
              />
              <div
                className="absolute -top-12 left-1/2 -translate-x-1/2 h-44 w-44 rounded-full overflow-hidden border-[6px] border-white shadow-[0_18px_40px_rgba(28,22,40,0.18)] flex items-center justify-center group-hover:scale-105 group-hover:rotate-3 transition-transform duration-300"
                style={{ background: "#ffffff" }}
              >
                {b.image ? (
                  <img
                    src={b.image}
                    alt={b.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="font-display font-black text-pb-brand-ink text-5xl">{b.name.charAt(0)}</span>
                )}
              </div>
              <div className="relative text-center">
                <h3 className="font-display font-black text-2xl mt-2 text-pb-brand-ink">{b.name}</h3>
                <p
                  className="text-xs font-extrabold uppercase tracking-wider mt-1"
                  style={{ color: b.color }}
                >
                  {b.tagline}
                </p>
                {b.purpose && (
                  <p className="text-pb-brand-ink mt-3 leading-relaxed text-sm font-bold italic px-2">
                    {b.purpose}
                  </p>
                )}
                <p className="text-pb-brand-ink/70 mt-2 leading-relaxed text-sm">{b.personality}</p>
                <div className="mt-5 flex flex-wrap gap-2 justify-center">
                  {b.favorite_foods.slice(0, 3).map((f) => (
                    <span
                      key={f}
                      className="bg-pb-cream text-pb-brand-ink text-xs font-bold px-3 py-1 rounded-full border border-pb-brand-ink/10"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <div
                  className="mt-6 inline-flex items-center gap-2 text-sm font-extrabold text-pb-brand-ink/80 group-hover:transition"
                  style={{ "--hover-color": b.color }}
                >
                  <Utensils className="h-4 w-4" /> {b.recipe}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div
          data-testid="modal-buddy"
          onClick={() => setActive(null)}
          className="fixed inset-0 z-50 bg-pb-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[2.5rem] max-w-2xl w-full p-8 sm:p-10 shadow-[0_30px_80px_rgba(28,22,40,0.25)]"
            style={{ borderTop: `8px solid ${active.color}` }}
          >
            <div className="flex items-start gap-5">
              <div
                className="h-28 w-28 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 border-[6px] border-white shadow-[0_14px_28px_rgba(0,0,0,0.18)]"
                style={{ background: "#ffffff" }}
              >
                {active.image ? (
                  <img src={active.image} alt={active.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display font-black text-white text-3xl">{active.name.charAt(0)}</span>
                )}
              </div>
              <div>
                <h3 className="font-display font-black text-3xl">{active.name}</h3>
                <p className="text-sm font-extrabold uppercase tracking-wider text-pb-coral mt-1">
                  {active.tagline}
                </p>
              </div>
            </div>
            {active.purpose && (
              <p data-testid="modal-buddy-purpose" className="mt-5 text-pb-ink font-bold italic leading-relaxed">
                {active.purpose}
              </p>
            )}
            <p className="mt-4 text-pb-soft leading-relaxed">{active.backstory}</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div className="rounded-2xl bg-pb-cream p-5 border border-pb-sky/30">
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral flex items-center gap-1">
                  <Heart className="h-3.5 w-3.5" /> Favorite foods
                </p>
                <ul className="mt-3 space-y-1 text-pb-ink font-bold">
                  {active.favorite_foods.map((f) => (
                    <li key={f}>• {f}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-pb-cream p-5 border border-pb-sky/30">
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral flex items-center gap-1">
                  <Utensils className="h-3.5 w-3.5" /> Coordinating recipe
                </p>
                <p className="mt-3 font-display font-black text-xl">{active.recipe}</p>
                <p className="text-sm text-pb-soft mt-1">One meal for the whole family — kid-friendly plating.</p>
                {recipeByBuddy[active.id] && (
                  <Link
                    to={`/recipes/${recipeByBuddy[active.id].id}`}
                    data-testid={`modal-recipe-link-${active.id}`}
                    onClick={() => setActive(null)}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-extrabold text-pb-coral hover:gap-2.5 transition-all"
                  >
                    View full recipe <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>

            <button
              data-testid="btn-close-buddy"
              onClick={() => setActive(null)}
              className="mt-8 w-full rounded-full bg-pb-ink text-white font-bold py-3 hover:bg-pb-coral transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
